<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  proxyRefs,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import KioskStepProgressIndicator from '@/app/layouts/kiosk/KioskStepProgressIndicator.vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { MAX_FACES } from '@/services/FaceDetection';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import FaceDemographicsDropdown from './faces/FaceDemographicsDropdown.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import FaceSelectionActions from './faces/FaceSelectionActions.vue';
// biome-ignore lint/style/useImportType: component is rendered in <template>
import FaceSelectionGallery from './faces/FaceSelectionGallery.vue';
import type {
  FaceDemographicOption,
  FaceDropdownPanelStyle,
  FaceDropdownState,
} from './faces/faceSelectionTypes';
import {
  type FaceSelectionFlow,
  useFaceSelectionFlow,
} from './faces/useFaceSelectionFlow';
import {
  type FaceSelectionScroller,
  useFaceSelectionScroller,
} from './faces/useFaceSelectionScroller';

interface FacesPublicApi {
  readonly $refs: { scrollable: HTMLElement | null };
  _isConfirming: boolean;
  _isMounted: boolean;
  readonly ageOptions: FaceDemographicOption[];
  closeDropdown: FaceSelectionFlow['closeDropdown'];
  readonly demographicsConfirmations: string[];
  readonly dropdownOptions: FaceDemographicOption[];
  readonly dropdownPanelStyle: FaceDropdownPanelStyle;
  dropdownState: FaceDropdownState | null;
  readonly genderOptions: FaceDemographicOption[];
  getDropdownLabel: FaceSelectionFlow['getDropdownLabel'];
  isFaceSelected: FaceSelectionFlow['isFaceSelected'];
  isScrollableLeftArrowVisible: boolean;
  isScrollableRightArrowVisible: boolean;
  onAgeChange: FaceSelectionFlow['onAgeChange'];
  onConfirmClick: FaceSelectionFlow['onConfirmClick'];
  onFaceClick: FaceSelectionFlow['onFaceClick'];
  onGenderChange: FaceSelectionFlow['onGenderChange'];
  onRaceChange: FaceSelectionFlow['onRaceChange'];
  onSelectArrowClick: FaceSelectionScroller['onSelectArrowClick'];
  onSelectScroll: FaceSelectionScroller['onSelectScroll'];
  onSelectScrollThrottled: FaceSelectionScroller['onSelectScrollThrottled'];
  openDropdown: FaceSelectionFlow['openDropdown'];
  readonly raceOptions: FaceDemographicOption[];
  readonly router: typeof router;
  selectDropdownOption: FaceSelectionFlow['selectDropdownOption'];
  readonly showAgeSelector: boolean;
  readonly showGenderSelector: boolean;
  readonly showNonBinaryGender: boolean;
  readonly showRaceSelector: boolean;
  readonly store: typeof store;
  readonly ui: ReturnType<typeof useUiStore>;
  updateScrollArrowVisibility: FaceSelectionScroller['updateScrollArrowVisibility'];
}

const { t } = useI18n();
const ui = useUiStore();
const gallery =
  useTemplateRef<InstanceType<typeof FaceSelectionGallery>>('gallery');

const scroller = useFaceSelectionScroller({
  getScrollable: (): HTMLElement | null =>
    gallery.value?.getScrollable() ?? null,
});
const {
  isScrollableLeftArrowVisible,
  isScrollableRightArrowVisible,
  onSelectArrowClick,
  onSelectScroll,
  onSelectScrollThrottled,
  updateScrollArrowVisibility,
}: FaceSelectionScroller = scroller;

const flow = useFaceSelectionFlow({
  cancelPendingScroll: scroller.leave,
  translate: t,
  updateScrollArrowVisibility,
});
const {
  ageOptions,
  closeDropdown,
  demographicsConfirmations,
  dropdownOptions,
  dropdownPanelStyle,
  dropdownState,
  enter,
  genderOptions,
  getDropdownLabel,
  isConfirming,
  isFaceSelected,
  isMounted,
  leave,
  onAgeChange,
  onConfirmClick,
  onFaceClick,
  onGenderChange,
  onRaceChange,
  openDropdown,
  raceOptions,
  selectDropdownOption,
  showAgeSelector,
  showGenderSelector,
  showNonBinaryGender,
  showRaceSelector,
}: FaceSelectionFlow = flow;

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const selectedFaceUuids = computed<string[]>(() =>
  store.facesSelected.map((face) => face.uuid)
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const isSelectionFull = computed(() => store.facesSelected.length >= MAX_FACES);

const publicApi: FacesPublicApi = proxyRefs({
  get $refs(): { scrollable: HTMLElement | null } {
    return { scrollable: gallery.value?.getScrollable() ?? null };
  },
  _isConfirming: isConfirming,
  _isMounted: isMounted,
  get ageOptions(): FaceDemographicOption[] {
    return ageOptions.value;
  },
  closeDropdown,
  get demographicsConfirmations(): string[] {
    return demographicsConfirmations.value;
  },
  get dropdownOptions(): FaceDemographicOption[] {
    return dropdownOptions.value;
  },
  get dropdownPanelStyle(): FaceDropdownPanelStyle {
    return dropdownPanelStyle.value;
  },
  dropdownState,
  get genderOptions(): FaceDemographicOption[] {
    return genderOptions.value;
  },
  getDropdownLabel,
  isScrollableLeftArrowVisible,
  isScrollableRightArrowVisible,
  isFaceSelected,
  onAgeChange,
  onConfirmClick,
  onFaceClick,
  onGenderChange,
  onRaceChange,
  onSelectArrowClick,
  onSelectScroll,
  onSelectScrollThrottled,
  openDropdown,
  get raceOptions(): FaceDemographicOption[] {
    return raceOptions.value;
  },
  router,
  selectDropdownOption,
  get showAgeSelector(): boolean {
    return showAgeSelector.value;
  },
  get showGenderSelector(): boolean {
    return showGenderSelector.value;
  },
  get showNonBinaryGender(): boolean {
    return showNonBinaryGender.value;
  },
  get showRaceSelector(): boolean {
    return showRaceSelector.value;
  },
  store,
  ui,
  updateScrollArrowVisibility,
});

onMounted((): void => {
  exposeToGlobalScope(publicApi, 'Faces');
  enter();
});

onBeforeUnmount(leave);

defineExpose(publicApi);
</script>

<template>
  <KioskLayout :is-centered="true">
    <template #KioskLayout__bottom>
      <KioskStepProgressIndicator :index="1" />
    </template>

    <div
      class="FacesIndividual"
      data-testid="faces-root"
      :class="{
        'FacesIndividual--single': store.faces.length === 1,
        'FacesIndividual--2': store.faces.length === 2,
        'FacesIndividual--3': store.faces.length === 3,
        'FacesIndividual--dense': store.faces.length > 6,
      }"
    >
      <FaceSelectionGallery
        ref="gallery"
        :faces="store.faces"
        :get-dropdown-label="getDropdownLabel"
        :is-scrollable-left-arrow-visible="isScrollableLeftArrowVisible"
        :is-scrollable-right-arrow-visible="isScrollableRightArrowVisible"
        :selected-face-uuids="selectedFaceUuids"
        :selection-full="isSelectionFull"
        :show-age-selector="showAgeSelector"
        :show-gender-selector="showGenderSelector"
        :show-race-selector="showRaceSelector"
        @arrow-click="onSelectArrowClick"
        @dropdown-open="openDropdown"
        @face-click="onFaceClick"
        @scroll="onSelectScrollThrottled"
      />

      <FaceSelectionActions
        :has-selection="store.facesSelected.length > 0"
        @confirm="onConfirmClick"
      />
    </div>

    <FaceDemographicsDropdown
      :options="dropdownOptions"
      :panel-style="dropdownPanelStyle"
      :state="dropdownState"
      @close="closeDropdown"
      @select="selectDropdownOption"
    />
  </KioskLayout>
</template>

<style lang="scss" scoped>
.FacesIndividual {
  --FacesIndividual__select-item-width: 13rem;
  --FacesIndividual__select-item-ratio: 0.84;
  --FacesIndividual__select-item-height: calc(var(--FacesIndividual__select-item-width) / var(--FacesIndividual__select-item-ratio));
  --FacesIndividual__select-item-gap: 3.8rem;
  --FacesIndividual__select-rows: 2;
  --FacesIndividual__select-list-height: calc(var(--FacesIndividual__select-item-height) * var(--FacesIndividual__select-rows));
  --FacesIndividual__select-arrow-width: calc(var(--FacesIndividual__select-item-width) * 0.35);
  --FacesIndividual__select-section-gap: 5rem;
  --FacesIndividual__select-scrollable-width: 48rem;
  --FacesIndividual__select-scrollable-max-width: none;
  --FacesIndividual__select-scrollable-overflow-x: auto;
  --FacesIndividual__select-scrollable-overflow-y: visible;
  --FacesIndividual__select-scrollable-padding: 0 var(--FacesIndividual__select-arrow-width) 0 0;
  --FacesIndividual__select-list-display: flex;
  --FacesIndividual__select-list-grid-template-columns: none;
  --FacesIndividual__select-list-gap: normal;
  --FacesIndividual__select-list-flex-direction: column;
  --FacesIndividual__select-list-align-items: normal;
  --FacesIndividual__select-list-justify-content: normal;
  --FacesIndividual__select-list-rendered-height: 100%;
  --FacesIndividual__select-list-max-height: var(--FacesIndividual__select-list-height);
  --FacesIndividual__select-list-max-width: none;
  --FacesIndividual__select-list-margin-left: 0;
  --FacesIndividual__select-arrow-display: block;
  --FacesIndividual__select-side-padding-display: list-item;
  --FacesIndividual__select-item-padding: calc(var(--FacesIndividual__select-item-gap) / 2);
  --FacesIndividual__dropdown-trigger-height: 2.6rem;
  --FacesIndividual__dropdown-trigger-font-size: 1.05rem;
  --FacesIndividual__info-width: 41rem;
  --FacesIndividual__info-align-items: normal;
  --FacesIndividual__info-padding: calc(var(--FacesIndividual__select-item-gap) / 2) 0 calc(var(--FacesIndividual__select-item-gap) / 2) var(--FacesIndividual__select-section-gap);
  --FacesIndividual__info-title-text-align: left;
  --FacesIndividual__info-title-margin: 0 0 2.5rem 0;
  --FacesIndividual__info-button-margin-top: 30px;

  display: flex;
}

.FacesIndividual--2,
.FacesIndividual--3 {
  --FacesIndividual__select-side-padding-display: none;
  --FacesIndividual__select-list-flex-direction: row;
  --FacesIndividual__select-list-align-items: center;
}

.FacesIndividual--2 {
  --FacesIndividual__select-item-width: 17rem;
  --FacesIndividual__select-list-justify-content: space-evenly;
}

.FacesIndividual--3 {
  --FacesIndividual__select-item-width: 14rem;
  --FacesIndividual__select-rows: 3;
  --FacesIndividual__select-list-justify-content: space-evenly;
}

:global(:root[data-kiosk-display-profile='vertical'] .FacesIndividual) {
  --FacesIndividual__select-item-width: calc((70vw - 2 * 1.5rem) / 3);
  --FacesIndividual__select-item-gap: 1.5rem;
  --FacesIndividual__select-section-gap: 1.5rem;
  --FacesIndividual__select-rows: 2;
  --FacesIndividual__select-scrollable-width: 70vw;
  --FacesIndividual__select-scrollable-max-width: 70rem;
  --FacesIndividual__select-scrollable-overflow-x: visible;
  --FacesIndividual__select-scrollable-padding: 0;
  --FacesIndividual__select-columns: 3;
  --FacesIndividual__select-list-display: grid;
  --FacesIndividual__select-list-grid-template-columns: repeat(var(--FacesIndividual__select-columns), var(--FacesIndividual__select-item-width));
  --FacesIndividual__select-list-gap: var(--FacesIndividual__select-item-gap);
  --FacesIndividual__select-list-justify-content: center;
  --FacesIndividual__select-list-rendered-height: auto;
  --FacesIndividual__select-list-max-height: none;
  --FacesIndividual__select-arrow-display: none;
  // The side padding items exist for the horizontal scroller; in the grid each
  // one takes a cell two rows high and pushes the confirm button off screen.
  --FacesIndividual__select-side-padding-display: none;
  --FacesIndividual__select-item-padding: 0;
  --FacesIndividual__dropdown-trigger-height: 2.4rem;
  --FacesIndividual__dropdown-trigger-font-size: 0.85rem;
  --FacesIndividual__info-width: 100%;
  --FacesIndividual__info-align-items: center;
  --FacesIndividual__info-padding: 2rem 0 0 0;
  --FacesIndividual__info-title-text-align: center;
  --FacesIndividual__info-title-margin: 0 0 1.5rem 0;
  --FacesIndividual__info-button-margin-top: 0;

  flex-direction: column;
  align-items: center;
}

:global(:root[data-kiosk-display-profile='vertical'] .FacesIndividual--single) {
  --FacesIndividual__select-columns: 1;
}

// Three rows of three cards and the confirm button do not fit the screen height.
:global(:root[data-kiosk-display-profile='vertical'] .FacesIndividual--dense) {
  --FacesIndividual__select-columns: 4;
  --FacesIndividual__select-item-width: calc((90vw - 3 * 1.5rem) / 4);
  --FacesIndividual__select-scrollable-width: 90vw;
}
</style>
